import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';

export interface CollectionFollow {
  id: string;
  follower_id: string;
  collection_id: string;
  created_at: string;
}

export function useCollectionFollows(collectionId?: string) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  

  // Get all collections the user follows
  const { data: followedCollections = [], isLoading: isLoadingFollowed } = useQuery({
    queryKey: ['collection-follows', user?.id],
    queryFn: async () => {
      if (!user) return [];

      const { data, error } = await supabase
        .from('collection_follows')
        .select('*')
        .eq('follower_id', user.id);

      if (error) throw error;
      return data as CollectionFollow[];
    },
    enabled: !!user,
  });

  // Check if user is following a specific collection
  const isFollowing = collectionId
    ? followedCollections.some((f) => f.collection_id === collectionId)
    : false;

  // Get follower count for a collection (for collection owners)
  const { data: followerCount = 0, isLoading: isLoadingFollowerCount } = useQuery({
    queryKey: ['collection-follower-count', collectionId],
    queryFn: async () => {
      if (!collectionId) return 0;

      const { count, error } = await supabase
        .from('collection_follows')
        .select('*', { count: 'exact', head: true })
        .eq('collection_id', collectionId);

      if (error) throw error;
      return count || 0;
    },
    enabled: !!collectionId,
  });

  // Follow a collection
  const followMutation = useMutation({
    mutationFn: async (targetCollectionId: string) => {
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('collection_follows')
        .insert({
          follower_id: user.id,
          collection_id: targetCollectionId,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (_, targetCollectionId) => {
      queryClient.invalidateQueries({ queryKey: ['collection-follows', user?.id] });
      queryClient.invalidateQueries({ queryKey: ['collection-follower-count', targetCollectionId] });
      // Followed silently
    },
    onError: (error: any) => {
      console.error('Follow error:', error);
      toast.error('Could not follow collection', {
        description: error.message || 'Please try again',
      });
    },
  });

  // Unfollow a collection
  const unfollowMutation = useMutation({
    mutationFn: async (targetCollectionId: string) => {
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('collection_follows')
        .delete()
        .eq('follower_id', user.id)
        .eq('collection_id', targetCollectionId);

      if (error) throw error;
    },
    onSuccess: (_, targetCollectionId) => {
      queryClient.invalidateQueries({ queryKey: ['collection-follows', user?.id] });
      queryClient.invalidateQueries({ queryKey: ['collection-follower-count', targetCollectionId] });
      // Unfollowed silently
    },
    onError: (error: any) => {
      console.error('Unfollow error:', error);
      toast.error('Could not unfollow collection', {
        description: error.message || 'Please try again',
      });
    },
  });

  const followCollection = (targetCollectionId: string) => {
    followMutation.mutate(targetCollectionId);
  };

  const unfollowCollection = (targetCollectionId: string) => {
    unfollowMutation.mutate(targetCollectionId);
  };

  const toggleFollow = (targetCollectionId: string) => {
    const currentlyFollowing = followedCollections.some(
      (f) => f.collection_id === targetCollectionId
    );
    if (currentlyFollowing) {
      unfollowCollection(targetCollectionId);
    } else {
      followCollection(targetCollectionId);
    }
  };

  return {
    followedCollections,
    isFollowing,
    followerCount,
    isLoading: isLoadingFollowed,
    isLoadingFollowerCount,
    followCollection,
    unfollowCollection,
    toggleFollow,
    isFollowPending: followMutation.isPending,
    isUnfollowPending: unfollowMutation.isPending,
  };
}
